import { useEffect, useState } from 'react';
import { api } from '../../api.ts';
import { formatEuros } from '../../format.ts';

interface Row {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  attestation: { valid: boolean; reason: 'NONE' | 'EXPIRED' | 'OUTDATED' | null; attestedAt: string | null; expiresAt: string | null };
  dependence: {
    status: 'NOT_EVALUATED' | 'OK' | 'WATCH' | 'EXCEEDED' | 'BLOCKED';
    totalCents: number;
    top: { name: string; sharePct: number; cents: number } | null;
    graceEndsAt: string | null;
  };
}

const DEPENDENCE: Record<Row['dependence']['status'], { label: string; badge: string }> = {
  NOT_EVALUATED: { label: 'Non évaluée', badge: 'badge-neutral' },
  OK: { label: 'Équilibrée', badge: 'badge-available' },
  WATCH: { label: 'Vigilance', badge: 'badge-pending' },
  EXCEEDED: { label: 'Dépassement', badge: 'badge-unavailable' },
  BLOCKED: { label: 'Bloqué', badge: 'badge-unavailable' },
};

const ATTESTATION_REASONS = { NONE: 'Jamais signée', EXPIRED: 'Expirée', OUTDATED: 'Texte mis à jour' } as const;

const FILTERS = [
  ['all', 'Tous'],
  ['issues', 'À surveiller'],
] as const;

export function AdminCompliance() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number][0]>('issues');

  useEffect(() => {
    api.get<{ extras: Row[] }>('/admin/extras-compliance').then((r) => setRows(r.extras));
  }, []);

  if (!rows) return <div className="empty-state">Chargement…</div>;
  const flagged = (r: Row) => !r.attestation.valid || r.dependence.status === 'EXCEEDED' || r.dependence.status === 'BLOCKED';
  const shown = filter === 'all' ? rows : rows.filter(flagged);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Suivi de l'attestation « plus d'un client » et de la dépendance économique (aucun client au-dessus du seuil de chiffre d'affaires sur 12 mois).
        Les seuils, le délai de régularisation et le blocage se règlent dans « Paramètres ».
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {FILTERS.map(([value, label]) => (
          <button key={value} className={`admin-nav-link${filter === value ? ' active' : ''}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => setFilter(value)}>
            {label}{value === 'issues' ? ` (${rows.filter(flagged).length})` : ` (${rows.length})`}
          </button>
        ))}
      </div>

      {shown.length === 0 && <div className="empty-state">{filter === 'issues' ? 'Aucun extra à surveiller.' : 'Aucun extra.'}</div>}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Extra</th><th>Attestation</th><th>CA 12 mois</th><th>Client principal</th><th>Dépendance</th></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.name}{!r.isActive && ' (suspendu)'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.email}</div>
                  </td>
                  <td>
                    {r.attestation.valid ? (
                      <span className="badge badge-available">À jour</span>
                    ) : (
                      <span className="badge badge-unavailable">{r.attestation.reason ? ATTESTATION_REASONS[r.attestation.reason] : 'Invalide'}</span>
                    )}
                  </td>
                  <td>{formatEuros(r.dependence.totalCents)}</td>
                  <td>{r.dependence.top ? `${r.dependence.top.name} · ${Math.round(r.dependence.top.sharePct)} %` : '—'}</td>
                  <td>
                    <span className={`badge ${DEPENDENCE[r.dependence.status].badge}`}>{DEPENDENCE[r.dependence.status].label}</span>
                    {r.dependence.graceEndsAt && r.dependence.status === 'EXCEEDED' && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        Blocage le {new Date(r.dependence.graceEndsAt).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
